import {useEffect, useMemo, useState} from 'react';
import type {ConnectedSmtRuntimePort} from '../runtime-port.ts';
import type {
  SmtDineInActionDescriptor,
  SmtDineInQueueRowViewModel,
  SmtDineInTableViewModel,
  SmtDineInViewModel,
} from '../../../../packages/runtime/smt-dine-in-contract.ts';

const actionLabel:Record<SmtDineInActionDescriptor['actionId'],string>={
  'table.open-walk-in':'開枱',
  'table.transfer':'轉枱',
  'table.merge':'合併枱',
  'queue.call':'叫號',
  'queue.seat':'安排入座',
  'table.mark-paid-continuing':'已付款續坐',
  'table.pay-and-close':'收款並關枱',
};
const tableStateLabel={available:'可用',occupied:'使用中',disabled:'停用',partitioned:'已分拆'} as const;
const queueStateLabel={waiting:'等候',called:'已叫號',seated:'已入座',left:'已離開',cancelled:'已取消'} as const;
const attentionLabel:Record<string,string>={
  none:'',
  'payment-outstanding':'待收款',
  'financial-data-incomplete':'財務資料未完整',
  'currency-mismatch':'幣別異常',
  disabled:'停用',
  partitioned:'已分拆',
};
const money=(minor:number|undefined,currency='HKD')=>minor===undefined?'—':new Intl.NumberFormat('zh-HK',{style:'currency',currency,currencyDisplay:'narrowSymbol',minimumFractionDigits:0,maximumFractionDigits:2}).format(minor/100);
const clock=(value:string|undefined)=>value?new Intl.DateTimeFormat('zh-HK',{hour:'2-digit',minute:'2-digit',hour12:false}).format(new Date(value)):'—';
const errorText=(error:unknown)=>error instanceof Error?error.message:'SMT_DINE_IN_UNKNOWN_ERROR';
const newOpaqueId=(prefix:string)=>`${prefix}:${globalThis.crypto?.randomUUID?.()??`${Date.now()}-${Math.random().toString(36).slice(2)}`}`;

type Interaction=
  | {kind:'transfer';sessionId:string}
  | {kind:'merge';primarySessionId:string}
  | {kind:'seat';queueEntryId:string}
  | null;

export function DineInPage({runtime}:{runtime:ConnectedSmtRuntimePort}){
  const [view,setView]=useState<SmtDineInViewModel|null>(null);
  const [selectedSessionId,setSelectedSessionId]=useState<string|null>(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState<string|null>(null);
  const [interaction,setInteraction]=useState<Interaction>(null);
  const [walkInTable,setWalkInTable]=useState<SmtDineInTableViewModel|null>(null);
  const [partySize,setPartySize]=useState(2);
  const request=useMemo(()=>selectedSessionId?{selectedSessionId}:{} as const,[selectedSessionId]);

  useEffect(()=>{
    let ignore=false;
    setLoading(true);
    runtime.readDineIn(request).then(next=>{if(!ignore){setView(next);setError(null);setLoading(false);}}).catch(reason=>{if(!ignore){setError(errorText(reason));setLoading(false);}});
    return()=>{ignore=true;};
  },[runtime,request]);

  const mutate=async(intent:Parameters<ConnectedSmtRuntimePort['dispatchDineIn']>[0])=>{
    setLoading(true);
    try{
      const next=await runtime.dispatchDineIn(intent,request);
      setView(next);
      setError(null);
      setInteraction(null);
    }catch(reason){setError(errorText(reason));}
    finally{setLoading(false);}
  };

  const context=()=>({expectedTableRevision:view?.tableRevision??0});
  const tableByArea=useMemo(()=>{
    const groups=new Map<string,SmtDineInTableViewModel[]>();
    for(const table of view?.tables??[]){const current=groups.get(table.areaId)??[];current.push(table);groups.set(table.areaId,current);}
    for(const tables of groups.values())tables.sort((a,b)=>a.position-b.position);
    return [...groups.entries()];
  },[view?.tables]);

  const selectTable=async(table:SmtDineInTableViewModel)=>{
    if(interaction?.kind==='transfer'){
      await mutate({type:'table.transfer',sessionId:interaction.sessionId,tableIds:[table.tableId],context:context()});return;
    }
    if(interaction?.kind==='merge'&&table.activeSessionId){
      await mutate({type:'table.merge',primarySessionId:interaction.primarySessionId,secondarySessionIds:[table.activeSessionId],context:context()});return;
    }
    if(interaction?.kind==='seat'){
      await mutate({type:'queue.seat',queueEntryId:interaction.queueEntryId,sessionId:newOpaqueId('session'),seatingGroupId:newOpaqueId('seating-group'),tableIds:[table.tableId],context:context()});return;
    }
    if(table.activeSessionId){setSelectedSessionId(table.activeSessionId);return;}
    const open=table.actions.find(action=>action.actionId==='table.open-walk-in');
    if(open?.enabled){setWalkInTable(table);setPartySize(2);}
  };

  const runSessionAction=(action:SmtDineInActionDescriptor)=>{
    const session=view?.selectedSession;
    if(!session||!action.enabled)return;
    if(action.actionId==='table.transfer'){setInteraction({kind:'transfer',sessionId:session.sessionId});return;}
    if(action.actionId==='table.merge'){setInteraction({kind:'merge',primarySessionId:session.sessionId});return;}
    if(action.actionId==='table.mark-paid-continuing')void mutate({type:'table.mark-paid-continuing',sessionId:session.sessionId,context:context()});
    if(action.actionId==='table.pay-and-close')void mutate({type:'table.pay-and-close',sessionId:session.sessionId,context:context()});
  };

  const runQueueAction=(row:SmtDineInQueueRowViewModel,action:SmtDineInActionDescriptor)=>{
    if(!action.enabled)return;
    if(action.actionId==='queue.call')void mutate({type:'queue.call',queueEntryId:row.queueEntryId,context:context()});
    if(action.actionId==='queue.seat')setInteraction({kind:'seat',queueEntryId:row.queueEntryId});
  };

  const confirmWalkIn=()=>{
    if(!walkInTable||!Number.isSafeInteger(partySize)||partySize<1)return;
    void mutate({
      type:'table.open-walk-in',
      sessionId:newOpaqueId('session'),
      seatingGroupId:newOpaqueId('seating-group'),
      originGuestGroupId:newOpaqueId('guest-group'),
      partySize,
      tableIds:[walkInTable.tableId],
      context:context(),
    });
    setWalkInTable(null);
  };

  if(!view&&loading)return <main className="dine-in-page" aria-busy="true"><div className="dine-in-loading">讀取堂食狀態…</div></main>;
  if(!view)return <main className="dine-in-page"><div className="dine-in-error" role="alert">{error??'堂食 Runtime 未可用'}</div></main>;

  const selected=view.selectedSession;
  const interactionText=interaction?.kind==='transfer'?'請點選目標空枱':interaction?.kind==='merge'?'請點選另一張使用中枱':interaction?.kind==='seat'?'請點選安排入座嘅空枱':null;

  return <main className="dine-in-page" aria-busy={loading}>
    <section className="dine-in-board glass-panel" aria-label="堂食枱面">
      <div className="dine-in-board-head"><div><span className="eyebrow">TABLE FLOOR</span><h1>堂食枱面</h1></div><div className="dine-in-meta"><span>{view.businessDate}</span><b>Rev {view.tableRevision}</b></div></div>
      {error?<div className="dine-in-error" role="alert">{error}</div>:null}
      {interactionText?<div className="dine-in-target-mode"><b>{interactionText}</b><button onClick={()=>setInteraction(null)}>取消</button></div>:null}
      <div className="dine-in-areas">{tableByArea.map(([areaId,tables])=><section className="dine-in-area" key={areaId}><div className="dine-in-area-title"><b>{areaId}</b><span>{tables.length} 枱</span></div><div className="dine-in-table-grid">{tables.map(table=>{
        const attention=attentionLabel[table.attention.code]||table.attention.code;
        return <button key={table.tableId} type="button" className={`dine-in-table state-${table.state}`} disabled={!table.selectable} onClick={()=>void selectTable(table)} aria-label={`${table.label} ${tableStateLabel[table.state]}`}>
          <div className="table-card-top"><strong>{table.label}</strong><span>{tableStateLabel[table.state]}</span></div>
          <div className="table-card-body"><b>{table.partySize?`${table.partySize} 位`:'空枱'}</b><span>{table.openedAt?clock(table.openedAt):'可即時開枱'}</span><span>{table.orderCount?`${table.orderCount} 張單`:''}</span></div>
          <div className="table-card-bottom"><strong>{money(table.outstandingMinor,table.currency)}</strong>{attention?<small>{attention}</small>:null}</div>
        </button>;
      })}</div></section>)}</div>

      <section className="dine-in-queue" aria-label="等候隊列"><div className="dine-in-section-head"><div><span className="eyebrow">QUEUE</span><h2>等候／叫號</h2></div><b>{view.queue.length}</b></div><div className="dine-in-queue-list">{view.queue.length?view.queue.map(row=><article key={row.queueEntryId} className={`queue-row status-${row.status}`}><div className="queue-code"><strong>{row.displayCode}</strong><span>{queueStateLabel[row.status]}</span></div><div className="queue-copy"><b>{row.partySize} 位 · {row.itemCount} 件</b><span>{clock(row.createdAt)} 登記</span></div><div className="queue-actions">{row.actions.map(action=><button key={action.actionId} disabled={!action.enabled||loading} title={action.disabledReason} onClick={()=>runQueueAction(row,action)}>{actionLabel[action.actionId]}</button>)}</div></article>):<p className="empty-copy">目前冇等候客人。</p>}</div></section>
    </section>

    <aside className="dine-in-detail glass-panel" aria-label="堂食詳情">
      {selected?<><div className="dine-in-detail-head"><div><span className="eyebrow">CURRENT SESSION</span><h2>{selected.tableLabels.join('＋')}</h2><code>{selected.sessionId}</code></div><span className={`session-state ${selected.status}`}>{selected.status==='paid_continuing'?'已付款續坐':selected.status==='closed'?'已關枱':'使用中'}</span></div>
        <div className="session-kpis"><div><span>人數</span><b>{selected.partySize}</b></div><div><span>開枱</span><b>{clock(selected.openedAt)}</b></div><div><span>訂單</span><b>{selected.orderIds.length}</b></div></div>
        <div className="session-money"><div><span>總額</span><strong>{money(selected.totalMinor,selected.currency)}</strong></div><div><span>未收</span><strong>{money(selected.outstandingMinor,selected.currency)}</strong></div><div><span>付款</span><b>{selected.fullySettled?'已完成':'未完成'}</b></div></div>
        {selected.attention.code!=='none'?<div className="session-attention">{attentionLabel[selected.attention.code]||selected.attention.code}</div>:null}
        <section className="session-orders"><div className="dine-in-section-head"><h3>訂單</h3><span>{selected.orders.length}</span></div>{selected.orders.map(order=><article key={order.orderId}><div><b>{order.orderId}</b><span>Rev {order.orderRevision}</span></div><strong>{money(order.totalMinor,order.currency)}</strong><small>{order.fullySettled?'已收款':`未收 ${money(order.outstandingMinor,order.currency)}`}</small></article>)}</section>
        <div className="session-actions">{selected.actions.map(action=><button key={action.actionId} disabled={!action.enabled||loading} title={action.disabledReason} onClick={()=>runSessionAction(action)}>{actionLabel[action.actionId]}</button>)}</div>
      </>:<div className="dine-in-empty-detail"><span>▦</span><h2>選擇一張使用中枱</h2><p>詳情、訂單、付款狀態同可用操作全部由 C Runtime 組合後顯示。</p></div>}
    </aside>

    {walkInTable?<div className="dine-in-dialog-backdrop" role="presentation"><section className="dine-in-dialog" role="dialog" aria-modal="true" aria-labelledby="walk-in-title"><span className="eyebrow">WALK-IN</span><h2 id="walk-in-title">{walkInTable.label} 開枱</h2><label>人數<input type="number" min="1" inputMode="numeric" value={partySize} onChange={event=>setPartySize(Number(event.target.value))}/></label><div><button onClick={()=>setWalkInTable(null)}>取消</button><button className="primary" disabled={!Number.isSafeInteger(partySize)||partySize<1||loading} onClick={confirmWalkIn}>確認開枱</button></div></section></div>:null}
  </main>;
}
